/**
 * PresenceEmployeeCalendar — focused calendar for a single employee. Renders
 * either a weekly 7-column strip (each column = 1 day, big enough to show all
 * the shifts comfortably) or a classic month grid (~5 rows × 7 columns) when
 * the user wants a higher-level view of the whole month.
 *
 * Click on an empty area → opens the create modal pre-filled with that day.
 * Click on an existing shift → opens the edit modal. Past days show a small
 * status dot derived from the matching PresenceRecords (asistencia / aviso /
 * ausencia / fichaje sin planificación).
 *
 * The component is presentational: it does NOT fetch data. The parent passes
 * the shifts and presence records already filtered to the chosen range and
 * employee, and supplies the click handlers.
 */
import { useMemo } from 'react';
import { PlusIcon, CheckCircleIcon, XCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { PlannerResource, PlannerScheduledItem, PresenceRecord } from '@/services/api';
import { ABSENCE_STYLE } from './PresenceTab';

type ScopeMode = 'week' | 'month';

function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d: Date): Date {
  const x = startOfDay(d); const wd = x.getDay(); x.setDate(x.getDate() - ((wd + 6) % 7)); return x;
}
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

const LATE_TOLERANCE_MIN = 5;
type Status = { dot: string; title: string } | null;
function computeStatus(day: Date, shifts: PlannerScheduledItem[], records: PresenceRecord[], approvedAbsences: PlannerScheduledItem[] = []): Status {
  const isPastOrToday = startOfDay(day).getTime() <= startOfDay(new Date()).getTime();
  if (!isPastOrToday) return null;
  const hasShifts = shifts.length > 0;
  const ins = records.filter((r) => r.type === 'in').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const outs = records.filter((r) => r.type === 'out').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const hasIn = ins.length > 0;
  const hasOut = outs.length > 0;
  if (approvedAbsences.length > 0 && !hasIn && !hasOut) {
    const a = approvedAbsences[0];
    const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
    return { dot: style.dot, title: `${style.label} (justificada)` };
  }
  if (!hasShifts && !hasIn && !hasOut) return null;
  if (hasShifts && !hasIn && !hasOut) return { dot: 'bg-red-500', title: 'Ausencia' };
  if (!hasShifts && (hasIn || hasOut)) return { dot: 'bg-gray-400', title: 'Fichaje sin planificación' };
  const plannedStart = new Date(Math.min(...shifts.map((s) => +new Date(s.start))));
  const plannedEnd = new Date(Math.max(...shifts.map((s) => +new Date(s.end))));
  const tags: string[] = [];
  if (hasIn) {
    const delay = Math.floor((+new Date(ins[0].date) - +plannedStart) / 60000);
    if (delay > LATE_TOLERANCE_MIN) tags.push(`retraso ${delay} min`);
  }
  if (hasOut) {
    const early = Math.floor((+plannedEnd - +new Date(outs[outs.length - 1].date)) / 60000);
    if (early > LATE_TOLERANCE_MIN) tags.push(`salida anticipada ${early} min`);
  }
  if (!hasOut && hasIn) {
    return { dot: 'bg-amber-500', title: tags.length ? `Pendiente cerrar salida · ${tags.join(' · ')}` : 'Pendiente cerrar salida' };
  }
  if (tags.length === 0) return { dot: 'bg-green-500', title: 'Asistencia' };
  return { dot: 'bg-amber-500', title: tags.join(' · ') };
}

export default function PresenceEmployeeCalendar({
  employee, scope, anchor, items, absences = [], presence, canManage,
  onCellClick, onNewAbsence, onApproveAbsence, onRejectAbsence, onDeleteAbsence,
}: {
  employee: PlannerResource;
  scope: ScopeMode;
  anchor: Date;
  items: PlannerScheduledItem[];
  absences?: PlannerScheduledItem[];
  presence: PresenceRecord[];
  canManage: boolean;
  onCellClick: (day: Date, existing: PlannerScheduledItem[]) => void;
  onNewAbsence?: (day: Date) => void;
  onApproveAbsence?: (id: string) => void;
  onRejectAbsence?: (id: string) => void;
  onDeleteAbsence?: (id: string) => void;
}) {
  // Range covered (week → 7 días; mes → primer dia del mes hasta el ultimo)
  const range = useMemo(() => {
    if (scope === 'week') {
      const f = startOfWeek(anchor);
      const lastDay = addDays(f, 6);
      return { from: f, lastDay };
    }
    const f = startOfMonth(anchor);
    const lastDay = endOfMonth(anchor);
    return { from: f, lastDay };
  }, [scope, anchor]);

  // Cells: for week, 7 days. For month, pad to start on Mon and end on Sun so
  // the grid is a clean 7-column matrix (leading/trailing days belong to the
  // adjacent month and render faded).
  const cells = useMemo<{ date: Date; inScope: boolean }[]>(() => {
    if (scope === 'week') {
      const out: { date: Date; inScope: boolean }[] = [];
      for (let i = 0; i < 7; i++) out.push({ date: addDays(range.from, i), inScope: true });
      return out;
    }
    const monthFirst = range.from;
    const monthLast = range.lastDay;
    const gridStart = startOfWeek(monthFirst);
    const gridEndExclusive = addDays(startOfWeek(monthLast), 7);
    const out: { date: Date; inScope: boolean }[] = [];
    for (let d = new Date(gridStart); d < gridEndExclusive; d = addDays(d, 1)) {
      out.push({ date: new Date(d), inScope: d.getMonth() === monthFirst.getMonth() });
    }
    return out;
  }, [scope, range]);

  // Index shifts / presence by isoDay for fast lookup per cell.
  const itemsByDay = useMemo(() => {
    const m = new Map<string, PlannerScheduledItem[]>();
    for (const it of items) {
      if (it.resourceId !== employee._id) continue;
      const key = isoDay(new Date(it.start));
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
    for (const arr of m.values()) arr.sort((a, b) => +new Date(a.start) - +new Date(b.start));
    return m;
  }, [items, employee._id]);
  const presenceByDay = useMemo(() => {
    const m = new Map<string, PresenceRecord[]>();
    for (const p of presence) {
      if (employee.employeeNif && p.employeeNif !== employee.employeeNif) continue;
      const key = isoDay(new Date(p.date));
      (m.get(key) || m.set(key, []).get(key)!).push(p);
    }
    return m;
  }, [presence, employee.employeeNif]);
  const absencesByDay = useMemo(() => {
    const m = new Map<string, PlannerScheduledItem[]>();
    for (const a of absences) {
      if (a.resourceId !== employee._id) continue;
      const key = isoDay(new Date(a.start));
      (m.get(key) || m.set(key, []).get(key)!).push(a);
    }
    return m;
  }, [absences, employee._id]);

  const todayIso = isoDay(new Date());

  // --- WEEK STRIP ---
  if (scope === 'week') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {cells.map(({ date }) => {
            const iso = isoDay(date);
            const dayShifts = itemsByDay.get(iso) || [];
            const dayPresence = presenceByDay.get(iso) || [];
            const dayAbsences = absencesByDay.get(iso) || [];
            const approvedAbsences = dayAbsences.filter((a) => a.status === 'approved');
            const status = computeStatus(date, dayShifts, dayPresence, approvedAbsences);
            const isToday = iso === todayIso;
            return (
              <div key={iso}
                className={`bg-white min-h-[16rem] cursor-pointer hover:bg-gray-50 transition-colors ${isToday ? 'ring-2 ring-vida-500 ring-inset' : ''}`}
                onClick={() => onCellClick(date, dayShifts)}>
                <div className={`px-3 py-2 border-b border-gray-100 flex items-center justify-between ${isToday ? 'bg-vida-50' : 'bg-gray-50'}`}>
                  <div>
                    <div className="text-xs uppercase text-gray-500">{date.toLocaleDateString('es-ES', { weekday: 'long' })}</div>
                    <div className="text-sm font-semibold text-gray-900">{date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</div>
                  </div>
                  {status && <span className={`w-2.5 h-2.5 rounded-full ${status.dot}`} title={status.title} />}
                </div>
                <div className="p-2 space-y-1.5">
                  {dayAbsences.map((a) => {
                    const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
                    const isPending = a.status === 'pending';
                    return (
                      <div key={a._id}
                        onClick={(e) => e.stopPropagation()}
                        className={`px-2 py-1.5 rounded text-xs font-medium ${style.chip} ${isPending ? 'ring-2 ring-amber-500 opacity-80' : ''}`}>
                        <div className="flex items-center justify-between gap-1">
                          <span className="truncate">{style.label}{isPending ? ' · pendiente' : ''}</span>
                          {canManage && (
                            <div className="flex items-center gap-0.5">
                              {isPending && onApproveAbsence && (
                                <button onClick={() => onApproveAbsence(a._id)} title="Aprobar"
                                  className="p-0.5 hover:bg-white/20 rounded"><CheckCircleIcon className="h-3.5 w-3.5" /></button>
                              )}
                              {isPending && onRejectAbsence && (
                                <button onClick={() => onRejectAbsence(a._id)} title="Rechazar"
                                  className="p-0.5 hover:bg-white/20 rounded"><XCircleIcon className="h-3.5 w-3.5" /></button>
                              )}
                              {onDeleteAbsence && (
                                <button onClick={() => onDeleteAbsence(a._id)} title="Eliminar"
                                  className="p-0.5 hover:bg-white/20 rounded"><TrashIcon className="h-3.5 w-3.5" /></button>
                              )}
                            </div>
                          )}
                        </div>
                        {a.notes && <div className="text-[10px] opacity-80 truncate">{a.notes}</div>}
                      </div>
                    );
                  })}
                  {dayShifts.map((s) => (
                    <button key={s._id}
                      onClick={(e) => { e.stopPropagation(); onCellClick(date, [s]); }}
                      className="block w-full text-left px-2 py-1.5 rounded bg-vida-600 hover:bg-vida-700 text-white text-xs font-medium">
                      {fmtTime(s.start)} – {fmtTime(s.end)}
                    </button>
                  ))}
                  {/* Fichajes reales del dia (read-only en la celda; el modal
                      al pulsar la celda los muestra tambien con mas detalle). */}
                  {dayPresence.length > 0 && (
                    <div className="pt-1 mt-1 border-t border-amber-200 space-y-0.5">
                      {dayPresence
                        .slice()
                        .sort((a, b) => +new Date(a.date) - +new Date(b.date))
                        .map((p) => (
                          <div key={p._id} className="text-[11px] text-amber-800 flex items-center gap-1">
                            <span>{p.type === 'in' ? '⏵' : '⏸'}</span>
                            <span className="font-mono">{fmtTime(p.date)}</span>
                            <span className="text-amber-700">{p.type === 'in' ? 'Entrada' : 'Salida'}</span>
                            {p.source === 'auto' && <span className="text-[9px] text-amber-500">(auto)</span>}
                            {p.source === 'manual' && <span className="text-[9px] text-amber-500">(manual)</span>}
                          </div>
                        ))}
                    </div>
                  )}
                  {dayShifts.length === 0 && dayPresence.length === 0 && dayAbsences.length === 0 && canManage && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-center h-10 text-gray-300 hover:text-vida-600 text-xs">
                        <PlusIcon className="h-4 w-4 mr-1" /> Añadir turno
                      </div>
                      {onNewAbsence && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onNewAbsence(date); }}
                          className="block w-full text-center text-[11px] text-gray-400 hover:text-amber-600">
                          + ausencia
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // --- MONTH GRID ---
  const weekdayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {weekdayLabels.map((l) => (
          <div key={l} className="px-3 py-2 text-xs uppercase font-medium text-gray-500 text-center">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {cells.map(({ date, inScope }) => {
          const iso = isoDay(date);
          const dayShifts = itemsByDay.get(iso) || [];
          const dayPresence = presenceByDay.get(iso) || [];
          const dayAbsences = absencesByDay.get(iso) || [];
          const approvedAbsences = dayAbsences.filter((a) => a.status === 'approved');
          const status = computeStatus(date, dayShifts, dayPresence, approvedAbsences);
          const isToday = iso === todayIso;
          return (
            <div key={iso}
              className={`bg-white min-h-[6.5rem] p-1.5 cursor-pointer hover:bg-gray-50 transition-colors ${!inScope ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-vida-500 ring-inset' : ''}`}
              onClick={() => onCellClick(date, dayShifts)}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold ${isToday ? 'text-vida-700' : 'text-gray-700'}`}>{date.getDate()}</span>
                {status && <span className={`w-2 h-2 rounded-full ${status.dot}`} title={status.title} />}
              </div>
              <div className="space-y-0.5">
                {dayAbsences.slice(0, 2).map((a) => {
                  const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
                  const isPending = a.status === 'pending';
                  return (
                    <div key={a._id}
                      title={`${style.label}${isPending ? ' · pendiente' : ''}${a.notes ? ' · ' + a.notes : ''}`}
                      className={`px-1 py-0.5 rounded text-[10px] truncate ${style.chip} ${isPending ? 'ring-1 ring-amber-500 opacity-80' : ''}`}>
                      {style.label}
                    </div>
                  );
                })}
                {dayShifts.slice(0, 2).map((s) => (
                  <button key={s._id}
                    onClick={(e) => { e.stopPropagation(); onCellClick(date, [s]); }}
                    className="block w-full text-left px-1 py-0.5 rounded bg-vida-600 hover:bg-vida-700 text-white text-[10px] truncate">
                    {fmtTime(s.start)}–{fmtTime(s.end)}
                  </button>
                ))}
                {dayShifts.length > 2 && (
                  <div className="text-[10px] text-gray-500">+{dayShifts.length - 2} más</div>
                )}
                {/* Fichajes reales en formato muy compacto. */}
                {dayPresence.slice(0, 2).map((p) => (
                  <div key={p._id} className="text-[10px] text-amber-800 font-mono truncate">
                    {p.type === 'in' ? '⏵' : '⏸'} {fmtTime(p.date)}
                  </div>
                ))}
                {dayPresence.length > 2 && (
                  <div className="text-[10px] text-amber-700">+{dayPresence.length - 2} fich.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
